import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    LinearProgress,
    Paper,
    SvgIconProps,
    Typography,
} from '@mui/material';
import React, { ComponentType, useEffect, useMemo, useState } from 'react';
import { useLessonPlanRecording } from '../../context/LessonPlanRecordingContext';
import {
    useVoiceLessonSession,
    VoiceLessonPhase,
    VoiceLessonTranscriptEvent,
} from '../../hooks/useVoiceLessonSession';
import { LessonPlanSessionModel, SessionSlideModel } from '../../object-actions/types/types';
import { AdminIcon, ProfileIcon } from '../../theme/icons';
import { VoiceNavigationIntent } from './voiceNavigation';

interface LessonPlanFacilitatorSessionProps {
    lessonPlanId?: number | null;
    session: LessonPlanSessionModel;
    isPublished?: boolean;
    updateSlide?: (slideId: number, updates: Partial<SessionSlideModel>) => void;
    updateSession?: (sessionId: number, updates: Partial<LessonPlanSessionModel>) => void;
    onNavigationIntent?: (intent: VoiceNavigationIntent) => void;
    onVoiceActiveChange?: (voiceActive: boolean) => void;
}

const LessonPlanFacilitatorSession: React.FC<LessonPlanFacilitatorSessionProps> = ({
    lessonPlanId,
    session,
    isPublished = false,
    updateSlide,
    updateSession,
    onNavigationIntent,
    onVoiceActiveChange,
}) => {
    const [localError, setLocalError] = useState<string | null>(null);
    const recordingContext = useLessonPlanRecording();

    const {
        voiceActive,
        phase,
        voiceError,
        transcriptEvents,
        currentAiTranscript,
        currentSlideId,
        activeSlideIndex,
        totalSlides,
        startVoiceLesson,
        stopVoiceLesson,
        advanceToNextSlide,
        endSession,
    } = useVoiceLessonSession({
        lessonPlanId: lessonPlanId ?? null,
        session,
        onSlideProgress: updateSlide,
        onSessionProgress: updateSession,
        onNavigationIntent,
    });

    useEffect(() => {
        onVoiceActiveChange?.(voiceActive);
    }, [voiceActive, onVoiceActiveChange]);

    // Unmounting (collapse / edit mode) tears the voice session down, so the
    // parent must not be left thinking the facilitator is still driving slides.
    useEffect(() => () => onVoiceActiveChange?.(false), [onVoiceActiveChange]);

    const orderedSlides = useMemo(() => {
        const slides = (session.slides || []).filter((slide): slide is SessionSlideModel => Boolean(slide?.id));
        return [...slides].sort((a, b) => (a.slide_number ?? 0) - (b.slide_number ?? 0));
    }, [session.slides]);

    const currentSlide = useMemo(() => {
        return orderedSlides.find(slide => slide.id === currentSlideId) ?? null;
    }, [orderedSlides, currentSlideId]);

    const slideDisplayPosition = activeSlideIndex >= 0 ? activeSlideIndex + 1 : null;
    const isLastSlide = slideDisplayPosition != null && totalSlides > 0 && slideDisplayPosition === totalSlides;
    const showFacilitatorDetails =
        voiceActive ||
        phase === 'completed' ||
        phase === 'error' ||
        transcriptEvents.length > 0 ||
        Boolean(currentAiTranscript);

    const handleStart = async () => {
        setLocalError(null);
        if (!lessonPlanId || !session?.id || !isPublished) {
            setLocalError('The lesson plan must be published before starting the facilitator.');
            return;
        }
        // Browser session recording can run alongside the AI facilitator —
        // both consume getUserMedia, but most browsers reuse a single
        // permission grant. We no longer block the AI on recording state.
        if (recordingContext.isRecording() || recordingContext.isStarted) {
            voiceLessonRecordingNote();
        }
        await startVoiceLesson();
    };

    return (
        <Paper
            elevation={1}
            data-testid="lesson-plan-facilitator-session"
            sx={{
                p: 2,
                mb: 2,
                borderRadius: "0 8px 8px 0",
                border: theme => `1px solid ${theme.palette.divider}`,
                borderLeftWidth: 4
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        AI Lesson Facilitator <em>(Beta)</em>
                    </Typography>
                    {showFacilitatorDetails && (
                        <Typography variant="body2" color="text.secondary">
                            Voice-driven facilitation. Say &ldquo;next slide&rdquo; or use the buttons to advance.
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip
                        label={voicePhaseLabel(phase, slideDisplayPosition, totalSlides, currentSlide?.title)}
                        color={phase === 'error' ? 'error' : phase === 'completed' ? 'success' : voiceActive ? 'primary' : 'default'}
                        data-attr="lesson-plan-facilitator_phase"
                    />
                    {!voiceActive ? (
                        <Button
                            variant="contained"
                            startIcon={<RecordVoiceOverIcon />}
                            onClick={handleStart}
                            disabled={phase === 'connecting'}
                            data-attr="lesson-plan-facilitator_start"
                        >
                            Start Facilitator
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="contained"
                                startIcon={<SkipNextIcon />}
                                onClick={() => { void advanceToNextSlide(); }}
                                disabled={phase === 'connecting' || phase === 'processing' || isLastSlide}
                                data-attr="lesson-plan-facilitator_next-slide"
                            >
                                Next slide
                            </Button>
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={() => { void endSession('button'); }}
                                disabled={phase === 'connecting' || phase === 'processing'}
                                data-attr="lesson-plan-facilitator_end-session"
                            >
                                End session
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<StopCircleIcon />}
                                onClick={() => { void stopVoiceLesson(); }}
                                data-attr="lesson-plan-facilitator_stop"
                            >
                                Stop
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {(phase === 'connecting' || phase === 'processing') && (
                <LinearProgress sx={{ mt: 2 }} data-attr="lesson-plan-facilitator_progress" />
            )}

            {(localError || voiceError) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {localError || voiceError}
                </Alert>
            )}

            {showFacilitatorDetails && (
                <>
                    <Divider sx={{ mt: 2, mb: 2 }} />

                    {slideDisplayPosition != null && totalSlides > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="overline" color="text.secondary">
                                Slide {slideDisplayPosition} of {totalSlides}
                                {currentSlide?.title ? ` — ${currentSlide.title}` : ''}
                            </Typography>
                        </Box>
                    )}

                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            Session transcript
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {transcriptEvents.map(event => {
                                const row = transcriptRowForSpeaker(event.speaker);
                                const RowIcon = row.Icon;
                                return (
                                    <Box
                                        key={event.id}
                                        sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                                        aria-label={`${row.ariaLabel}: ${event.text}`}
                                    >
                                        <RowIcon
                                            fontSize="small"
                                            sx={{ color: row.iconColor, mt: 0.25, flexShrink: 0 }}
                                            aria-hidden
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                        >
                                            {event.text}
                                        </Typography>
                                    </Box>
                                );
                            })}
                            {currentAiTranscript ? (
                                <Box
                                    sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                                    aria-label={`AI facilitator (speaking): ${currentAiTranscript}`}
                                >
                                    <AdminIcon
                                        fontSize="small"
                                        sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }}
                                        aria-hidden
                                    />
                                    <Typography
                                        variant="body2"
                                        color="primary"
                                        sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                    >
                                        {currentAiTranscript}
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                </>
            )}
        </Paper>
    );
};

function transcriptRowForSpeaker(speaker: VoiceLessonTranscriptEvent['speaker']): {
    Icon: ComponentType<SvgIconProps>;
    iconColor: string;
    ariaLabel: string;
} {
    switch (speaker) {
        case 'ai':
            return { Icon: AdminIcon, iconColor: 'primary.main', ariaLabel: 'AI facilitator' };
        case 'participant':
            return { Icon: ProfileIcon, iconColor: 'text.secondary', ariaLabel: 'You' };
        case 'system':
            return { Icon: AdminIcon, iconColor: 'text.disabled', ariaLabel: 'System' };
    }
}

function voicePhaseLabel(
    phase: VoiceLessonPhase,
    slidePosition: number | null,
    totalSlides: number,
    slideTitle?: string,
): string {
    const slidePrefix = slidePosition != null && totalSlides > 0
        ? `Slide ${slidePosition}/${totalSlides}${slideTitle ? `: ${slideTitle}` : ''}`
        : '';
    switch (phase) {
        case 'connecting':
            return 'Connecting';
        case 'facilitating':
            return slidePrefix ? `Facilitating ${slidePrefix}` : 'Facilitating';
        case 'listening':
            return slidePrefix ? `Listening on ${slidePrefix}` : 'Listening';
        case 'processing':
            return 'Updating progress';
        case 'completed':
            return 'Completed';
        case 'error':
            return 'Error';
        default:
            return 'Ready';
    }
}

function voiceLessonRecordingNote() {
    if (typeof console !== 'undefined') {
        console.debug('[voice-lesson] AI facilitator starting alongside an active browser recording session.');
    }
}

export default LessonPlanFacilitatorSession;
